import { useCallback, useEffect, useMemo, useState } from "react";
import {
	Box,
	Chip,
	CircularProgress,
	LinearProgress,
	Paper,
	Stack,
	Typography,
	alpha,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import dayjs from "dayjs";
import axios from "axios";
import {
	REQUIRED_SPREADSHEET_FILE_TYPES,
	UPLOAD_CALENDAR_STATUS,
	buildUploadsByDateFromApi,
	getUploadDayStatus,
	toLocalDateKey,
} from "./uploadCalendarConfig";

const UPLOAD_CALENDAR_ENDPOINT = "/api/pos_reco/get_upload_calendar_status";

const STATUS_STYLES = {
	[UPLOAD_CALENDAR_STATUS.COMPLETE]: {
		palette: "success",
		label: "All uploaded",
		short: "Complete",
	},
	[UPLOAD_CALENDAR_STATUS.PARTIAL]: {
		palette: "warning",
		label: "Partial upload",
		short: "Partial",
	},
	[UPLOAD_CALENDAR_STATUS.NONE]: {
		palette: "error",
		label: "Nothing uploaded",
		short: "Missing",
	},
};

const STATUS_ORDER = [
	UPLOAD_CALENDAR_STATUS.COMPLETE,
	UPLOAD_CALENDAR_STATUS.PARTIAL,
	UPLOAD_CALENDAR_STATUS.NONE,
];

const normalizeUploadCalendarResult = (payload) => payload?.result || payload || {};

const UploadStatusDay = ({ day, uploadsByDate, todayKey, ...other }) => {
	const dateKey = day.format("YYYY-MM-DD");
	const status = getUploadDayStatus(dateKey, uploadsByDate, todayKey);
	const isToday = dateKey === todayKey;

	return (
		<PickersDay
			{...other}
			day={day}
			sx={(theme) => {
				if (!status) {
					return {
						fontWeight: isToday ? 700 : 500,
						...(isToday && {
							border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
						}),
					};
				}
				const accent = theme.palette[STATUS_STYLES[status].palette].main;
				return {
					position: "relative",
					fontWeight: 600,
					color: theme.palette.text.primary,
					backgroundColor: alpha(accent, 0.14),
					border: `1px solid ${alpha(accent, 0.28)}`,
					"&:hover": { backgroundColor: alpha(accent, 0.24) },
					"&::after": {
						content: '""',
						position: "absolute",
						bottom: 4,
						left: "50%",
						transform: "translateX(-50%)",
						width: 5,
						height: 5,
						borderRadius: "50%",
						backgroundColor: accent,
					},
					"&.Mui-selected": {
						backgroundColor: `${alpha(accent, 0.28)} !important`,
						color: `${theme.palette.text.primary} !important`,
						borderColor: accent,
						boxShadow: `inset 0 0 0 1px ${accent}`,
					},
					"&.Mui-selected:hover": {
						backgroundColor: `${alpha(accent, 0.36)} !important`,
					},
					"&.Mui-selected.Mui-focusVisible": {
						backgroundColor: `${alpha(accent, 0.32)} !important`,
					},
				};
			}}
		/>
	);
};

const StatCard = ({ palette, label, count, total }) => (
	<Box
		sx={(theme) => ({
			flex: 1,
			minWidth: 0,
			px: 1.75,
			py: 1.25,
			borderRadius: 1.5,
			border: "1px solid",
			borderColor: alpha(theme.palette[palette].main, 0.3),
			bgcolor: alpha(theme.palette[palette].main, 0.06),
		})}>
		<Stack direction="row" alignItems="center" spacing={0.75} mb={0.25}>
			<Box
				sx={(theme) => ({
					width: 8,
					height: 8,
					borderRadius: "50%",
					bgcolor: theme.palette[palette].main,
				})}
			/>
			<Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>
				{label}
			</Typography>
		</Stack>
		<Typography variant="h6" fontWeight={700} lineHeight={1.1}>
			{count}
			<Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
				/ {total} days
			</Typography>
		</Typography>
	</Box>
);

const FileStatusCard = ({ name, uploaded }) => (
	<Stack
		direction="row"
		alignItems="center"
		spacing={1}
		sx={(theme) => ({
			px: 1.25,
			py: 0.85,
			borderRadius: 1.25,
			border: "1px solid",
			borderColor: uploaded
				? alpha(theme.palette.success.main, 0.3)
				: alpha(theme.palette.divider, 1),
			bgcolor: uploaded ? alpha(theme.palette.success.main, 0.05) : "transparent",
			minWidth: 0,
		})}>
		<Box
			sx={(theme) => ({
				width: 22,
				height: 22,
				borderRadius: "50%",
				flexShrink: 0,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				color: uploaded ? "success.main" : "error.main",
				bgcolor: uploaded
					? alpha(theme.palette.success.main, 0.14)
					: alpha(theme.palette.error.main, 0.1),
			})}>
			{uploaded ? <CheckRoundedIcon sx={{ fontSize: 14 }} /> : <CloseRoundedIcon sx={{ fontSize: 14 }} />}
		</Box>
		<Typography
			variant="body2"
			noWrap
			title={name}
			sx={{
				flex: 1,
				minWidth: 0,
				fontWeight: uploaded ? 600 : 500,
				color: uploaded ? "text.primary" : "text.secondary",
			}}>
			{name}
		</Typography>
	</Stack>
);

const UploadCalendar = ({ refreshKey = 0 }) => {
	const [loading, setLoading] = useState(true);
	const [uploadsByDate, setUploadsByDate] = useState({});
	const [visibleMonth, setVisibleMonth] = useState(dayjs());
	const [selectedDate, setSelectedDate] = useState(dayjs());

	const todayKey = useMemo(() => toLocalDateKey(new Date()), []);

	const fetchCalendarData = useCallback(async (monthValue) => {
		setLoading(true);
		try {
			const response = await axios.get(UPLOAD_CALENDAR_ENDPOINT, {
				params: {
					year: monthValue.year(),
					month: monthValue.month() + 1,
				},
			});
			setUploadsByDate(buildUploadsByDateFromApi(normalizeUploadCalendarResult(response.data)));
		} catch {
			setUploadsByDate({});
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchCalendarData(visibleMonth);
	}, [fetchCalendarData, visibleMonth, refreshKey]);

	const selectedDateKey = selectedDate.format("YYYY-MM-DD");
	const selectedUploadedTypes = uploadsByDate[selectedDateKey] || new Set();
	const selectedStatus = getUploadDayStatus(selectedDateKey, uploadsByDate, todayKey);
	const uploadedCount = REQUIRED_SPREADSHEET_FILE_TYPES.filter((type) =>
		selectedUploadedTypes.has(type.id)
	).length;
	const totalTypes = REQUIRED_SPREADSHEET_FILE_TYPES.length;
	const completionPct = Math.round((uploadedCount / totalTypes) * 100);

	const monthSummary = useMemo(() => {
		const counts = {
			[UPLOAD_CALENDAR_STATUS.COMPLETE]: 0,
			[UPLOAD_CALENDAR_STATUS.PARTIAL]: 0,
			[UPLOAD_CALENDAR_STATUS.NONE]: 0,
			evaluated: 0,
		};
		const daysInMonth = visibleMonth.daysInMonth();
		for (let d = 1; d <= daysInMonth; d += 1) {
			const key = visibleMonth.date(d).format("YYYY-MM-DD");
			const status = getUploadDayStatus(key, uploadsByDate, todayKey);
			if (status) {
				counts[status] += 1;
				counts.evaluated += 1;
			}
		}
		return counts;
	}, [visibleMonth, uploadsByDate, todayKey]);

	const monthCompletionPct = monthSummary.evaluated
		? Math.round((monthSummary[UPLOAD_CALENDAR_STATUS.COMPLETE] / monthSummary.evaluated) * 100)
		: 0;

	return (
		<Paper
			elevation={0}
			sx={{
				mb: 3,
				borderRadius: 2,
				border: "1px solid",
				borderColor: "divider",
				bgcolor: "background.paper",
				overflow: "hidden",
			}}>
			{/* Header */}
			<Stack
				direction={{ xs: "column", sm: "row" }}
				alignItems={{ xs: "flex-start", sm: "center" }}
				justifyContent="space-between"
				spacing={1.5}
				sx={{ px: { xs: 2, md: 2.5 }, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
				<Stack direction="row" alignItems="center" spacing={1.25}>
					<Box
						sx={(theme) => ({
							width: 40,
							height: 40,
							borderRadius: 1.5,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							bgcolor: alpha(theme.palette.primary.main, 0.1),
							color: "primary.main",
						})}>
						<CalendarMonthRoundedIcon fontSize="small" />
					</Box>
					<Box>
						<Typography variant="subtitle1" fontWeight={700} lineHeight={1.25}>
							Daily upload calendar
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Validates daily uploads from BigQuery dump tables
						</Typography>
					</Box>
				</Stack>

				<Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
					{STATUS_ORDER.map((status) => (
						<Chip
							key={status}
							size="small"
							variant="outlined"
							color={STATUS_STYLES[status].palette}
							label={STATUS_STYLES[status].short}
							sx={{ fontWeight: 600 }}
						/>
					))}
				</Stack>
			</Stack>

			{/* Month overview strip */}
			<Box sx={{ px: { xs: 2, md: 2.5 }, pt: 2 }}>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					mb={1}
					spacing={1}
					flexWrap="wrap"
					useFlexGap>
					<Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.6 }}>
						{visibleMonth.format("MMMM YYYY")} overview
					</Typography>
					<Typography variant="caption" color="text.secondary" fontWeight={600}>
						{monthCompletionPct}% of tracked days fully uploaded
					</Typography>
				</Stack>

				<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
					{STATUS_ORDER.map((status) => (
						<StatCard
							key={status}
							palette={STATUS_STYLES[status].palette}
							label={STATUS_STYLES[status].short}
							count={monthSummary[status]}
							total={monthSummary.evaluated}
						/>
					))}
				</Stack>
			</Box>

			{/* Body: calendar + selected day details */}
			<Box
				sx={{
					mt: 2,
					display: "grid",
					gridTemplateColumns: { xs: "1fr", md: "auto 1fr" },
					alignItems: "stretch",
					borderTop: "1px solid",
					borderColor: "divider",
				}}>
				{/* Calendar */}
				<Box
					sx={{
						position: "relative",
						p: { xs: 1.5, md: 2 },
						borderRight: { md: "1px solid" },
						borderBottom: { xs: "1px solid", md: "none" },
						borderColor: { xs: "divider", md: "divider" },
					}}>
					{loading && (
						<Box
							sx={(theme) => ({
								position: "absolute",
								inset: 0,
								zIndex: 2,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								bgcolor: alpha(theme.palette.background.paper, 0.75),
								backdropFilter: "blur(2px)",
							})}>
							<CircularProgress size={26} thickness={4} />
						</Box>
					)}

					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DateCalendar
							value={selectedDate}
							onChange={(value) => value && setSelectedDate(value)}
							onMonthChange={(value) => {
								if (!value) return;
								setVisibleMonth(value);
							}}
							referenceDate={visibleMonth}
							disableFuture
							slots={{ day: UploadStatusDay }}
							slotProps={{ day: { uploadsByDate, todayKey } }}
							sx={{
								m: 0,
								"& .MuiPickersCalendarHeader-root": { pl: 1, pr: 1, mt: 0 },
								"& .MuiPickersCalendarHeader-label": { fontWeight: 700 },
								"& .MuiPickersDay-root": { fontSize: "0.8125rem" },
							}}
						/>
					</LocalizationProvider>
				</Box>

				{/* Selected day details */}
				<Box
					sx={(theme) => ({
						p: { xs: 2, md: 2.5 },
						display: "flex",
						flexDirection: "column",
						bgcolor: alpha(theme.palette.grey[500], 0.03),
					})}>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						spacing={1}
						mb={2}
						flexWrap="wrap"
						useFlexGap>
						<Box>
							<Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.6 }}>
								Selected day
							</Typography>
							<Typography variant="h6" fontWeight={700} lineHeight={1.2}>
								{selectedDate.format("ddd, DD MMM YYYY")}
							</Typography>
						</Box>
						{selectedStatus ? (
							<Chip
								color={STATUS_STYLES[selectedStatus].palette}
								label={STATUS_STYLES[selectedStatus].label}
								sx={{ fontWeight: 600 }}
							/>
						) : (
							<Chip variant="outlined" label="Not evaluated" sx={{ fontWeight: 600 }} />
						)}
					</Stack>

					{selectedStatus ? (
						<Box sx={{ mb: 2 }}>
							<Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
								<Typography variant="body2" color="text.secondary" fontWeight={600}>
									Upload progress
								</Typography>
								<Typography variant="body2" fontWeight={700}>
									{uploadedCount}/{totalTypes} files
								</Typography>
							</Stack>
							<LinearProgress
								variant="determinate"
								value={completionPct}
								color={STATUS_STYLES[selectedStatus].palette}
								sx={{
									height: 8,
									borderRadius: 99,
									bgcolor: (theme) => alpha(theme.palette.divider, 0.5),
								}}
							/>
						</Box>
					) : (
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Future dates are not evaluated yet.
						</Typography>
					)}

					<Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mb: 1 }}>
						Required files ({totalTypes})
					</Typography>

					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
							gap: 1,
						}}>
						{REQUIRED_SPREADSHEET_FILE_TYPES.map((type) => (
							<FileStatusCard
								key={type.id}
								name={type.name}
								uploaded={selectedUploadedTypes.has(type.id)}
							/>
						))}
					</Box>
				</Box>
			</Box>
		</Paper>
	);
};

export default UploadCalendar;
